import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardService } from '../services';
import './HorarioGuard.css';
import Modal from './common/Modal';
import MaintenancePage from '../pages/MaintenancePage';
import OutofHoursPage from '../pages/OutofHoursPage';

const HorarioGuard = ({ children }) => {
  const { usuario, esPresidente, logout } = useAuth();
  const navigate = useNavigate();
  const [estaBloqueado, setEstaBloqueado] = useState(false);
  const [enMantenimiento, setEnMantenimiento] = useState(false);
  const [horaActual, setHoraActual] = useState(new Date());
  const [estadoSistema, setEstadoSistema] = useState(null);
  const lastActivity = useRef(Date.now());
  const idleTimeoutMinutes = useRef(15);

  useEffect(() => {
    // Actividad del usuario para reiniciar el timer
    const resetActivity = () => {
      lastActivity.current = Date.now();
    };

    window.addEventListener('mousemove', resetActivity);
    window.addEventListener('keydown', resetActivity);
    window.addEventListener('click', resetActivity);
    window.addEventListener('scroll', resetActivity);

    return () => {
      window.removeEventListener('mousemove', resetActivity);
      window.removeEventListener('keydown', resetActivity);
      window.removeEventListener('click', resetActivity);
      window.removeEventListener('scroll', resetActivity);
    };
  }, []);

  useEffect(() => {
    // Si no hay usuario o es presidente, no aplicar bloqueo
    if (!usuario || esPresidente()) {
      setEstaBloqueado(false);
      return;
    }

    const verificarEstado = async () => {
      try {
        const estado = await dashboardService.getEstadoSistema();
        setEstadoSistema(estado);
        if (estado.nombre_sistema) {
          document.title = estado.nombre_sistema;
        }
        // Roles exentos
        const rolesExentos = estado.roles_exentos_bloqueo ? estado.roles_exentos_bloqueo.split(',').map(r => r.trim()) : [];
        const esExento = rolesExentos.includes(usuario?.role) || esPresidente();

        if (estado.mantenimiento_activo && !esPresidente()) {
          setEnMantenimiento(true);
        } else {
          setEnMantenimiento(false);
        }

        // Si NO está en mantenimiento, verificar el horario
        if (!estado.mantenimiento_activo && !esExento && estado.bloqueo_nocturno_activo) {
          const ahora = new Date();
          setHoraActual(ahora);

          const horaInicio = estado.hora_inicio_sesion || '06:00';
          const horaCierre = estado.hora_cierre_sesion || '22:00';

          const [inicioH, inicioM] = horaInicio.split(':').map(Number);
          const [cierreH, cierreM] = horaCierre.split(':').map(Number);

          const minutosAhora = ahora.getHours() * 60 + ahora.getMinutes();
          const minutosInicio = inicioH * 60 + inicioM;
          const minutosCierre = cierreH * 60 + cierreM;

          let bloqueado = false;
          if (minutosInicio > minutosCierre) {
            // Horario cruzado por la medianoche (ej. 22:00 a 06:00)
            if (minutosAhora < minutosInicio && minutosAhora > minutosCierre) {
              bloqueado = true;
            }
          } else {
            // Horario normal (ej. 06:00 a 22:00)
            if (minutosAhora < minutosInicio || minutosAhora > minutosCierre) {
              bloqueado = true;
            }
          }

          setEstaBloqueado(bloqueado);
        } else {
          setEstaBloqueado(false);
        }
        if (estado.tiempo_inactividad_minutos) {
          idleTimeoutMinutes.current = estado.tiempo_inactividad_minutos;
        }

        // Verificar inactividad
        const now = Date.now();
        const diffMinutes = (now - lastActivity.current) / 1000 / 60;
        if (diffMinutes >= idleTimeoutMinutes.current) {
          logout();
          navigate('/error/419');
          return;
        }

      } catch (error) {
        console.error('Error al verificar estado del sistema', error);
      }
    };
    verificarEstado();
    const interval = setInterval(verificarEstado, 30000);

    return () => clearInterval(interval);
  }, [usuario, esPresidente]);

  // Si está en mantenimiento y no es presidente, renderizar página de mantenimiento
  if (enMantenimiento) {
    return <MaintenancePage />;
  }

  // Si está bloqueado por horario, renderizar página de fuera de horario
  if (estaBloqueado) {
    return <OutofHoursPage horaActual={horaActual} config={estadoSistema} />;
  }

  // Render children normally
  return <>{children}</>;
};

export default HorarioGuard;
